import { useMemo, useState, useEffect, useRef } from 'react';
import '../../accounting/accounting.css';
import '../../modules/SettingsDetailPage.css'; // full-page `.sd-*` detail frame + the standard field grid
import './currentLanguage.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useReasonGate, useSaveFlow } from '../../../components/confirm';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import { runWithAuditReason } from '../../../api/client';
import { useLanguages, useUpdateLanguage, errMessage, type Language } from './systemMasters';

interface Props { onHome?: () => void; }

const yesNo = (v: boolean) => (v ? 'Yes' : 'No');

type Ticks = { chinese: boolean; english: boolean };
/** Legacy rule (LanguageController): only one language is current — ticking one clears the other.
 *  Unticking leaves the other as it is. */
const withTick = (cur: Ticks, key: keyof Ticks, on: boolean): Ticks =>
  on ? { chinese: key === 'chinese', english: key === 'english' } : { ...cur, [key]: false };

/**
 * Settings ▸ System Management ▸ Current Language — the UI-language toggle record (legacy
 * CurrentLanguage_ListView). List actions as legacy: Edit · Refresh · Export to · Search; columns
 * Chinese then English. The row icon opens the record in the Detail View, editable (the in-row editor was
 * removed on owner request 2026-10-15).
 *
 * A click on a row opens its detail READ-ONLY (view); the Edit button there — or the toolbar Edit on
 * a ticked row — turns on the save flow (owner request 2026-10-12). Legacy has no New or Delete here.
 */
export function CurrentLanguagePage({ onHome }: Props) {
  const notify = useNotify();
  const gateReason = useReasonGate();
  const runSave = useSaveFlow();
  const { data: rows = [], isFetching, refetch } = useLanguages();
  const loadMs = useFetchMs(isFetching);
  const update = useUpdateLanguage();

  const [search, setSearch] = useState('');

  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);

  // Detail: undefined = list; a row = that record open, read-only ('view') or editable ('edit').
  const [detail, setDetail] = useState<Language | undefined>(undefined);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [form, setForm] = useState({ chinese: false, english: false });
  const viewOnly = mode === 'view';
  const open = (r: Language, m: 'view' | 'edit') => { setDetail(r); setForm({ chinese: r.chinese, english: r.english }); setMode(m); };
  const close = () => setDetail(undefined);
  // Cancel / breadcrumb: ask before dropping unsaved edits (standing rule 15).
  const leave = () => { void guardedExit(close); };

  const editSelected = () => {
    const r = rows.find((x) => selectedIds.has(x.id));
    if (selectedIds.size === 1 && r) open(r, 'edit');
  };

  /** Detail save through the shared flow: reason gate → "Do you want to Save?" → audited update →
   *  "Saved successfully." → stay on the record or return to the list. */
  const doSave = async (after: 'stay' | 'close') => {
    if (!detail) return;
    const reason = await gateReason('Current Language', 'edit');
    if (reason === null) return;
    await runSave({
      save: async () => { await runWithAuditReason(reason, () => update.mutateAsync({ id: detail.id, ...form })); },
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (after === 'close') { close(); return; }
        setDetail({ ...detail, ...form });
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  useHotkeys(detail === undefined ? [] : viewOnly ? [
    { combo: 'alt+e', label: 'Edit', group: 'Current Language', order: 1, run: () => setMode('edit') },
    { combo: 'f2', label: 'Edit', group: 'Current Language', order: 2, run: () => setMode('edit') },
    { combo: 'esc', label: 'Close', group: 'Current Language', order: 3, allowInInput: false, run: close },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'Current Language', order: 1, run: () => void doSave('stay') },
    { combo: 'mod+enter', label: 'Save & Close', group: 'Current Language', order: 2, run: () => void doSave('close') },
    { combo: 'esc', label: 'Cancel', group: 'Current Language', order: 3, allowInInput: false, run: leave },
  ]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [`chinese ${yesNo(r.chinese)}`, `english ${yesNo(r.english)}`].some((v) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  const tick = (r: Language, key: 'chinese' | 'english') => (
    <span className={`cellcheck${r[key] ? ' on' : ''}`} role="img" aria-label={r[key] ? 'Enabled' : 'Disabled'} />
  );

  const columns: AppColumn<Language>[] = [
    {
      // Row icon: open the record's detail straight in edit mode.
      field: 'editopen', header: '', noExport: true, sortable: false, filterable: false, width: 44, align: 'center', text: () => '',
      render: (r) => (
        <button className="pts-icon-btn edit" title="Edit" aria-label="Open the language record for editing" onClick={(e) => { e.stopPropagation(); open(r, 'edit'); }}><i className="ti ti-edit" /></button>
      ),
    },
    // Legacy column order: Chinese, then English.
    { field: 'chinese', header: 'Chinese', width: 160, align: 'center', sortable: false, text: (r) => yesNo(r.chinese), render: (r) => tick(r, 'chinese') },
    { field: 'english', header: 'English', width: 160, align: 'center', sortable: false, text: (r) => yesNo(r.english), render: (r) => tick(r, 'english') },
  ];

  // ── Full-page detail — replaces the list while open (standing rule 12). ──
  if (detail !== undefined) {
    const actions = (bottom?: boolean) => viewOnly ? (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="sd-abtn cancel" onClick={close} title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
      </div>
    ) : (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => void doSave('stay')} disabled={update.isPending} title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> Save</button>
        <button className="sd-abtn" onClick={() => void doSave('close')} disabled={update.isPending} title={`Save and Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save and Close</button>
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" onClick={leave} disabled={update.isPending} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
      </div>
    );
    const box = (key: 'chinese' | 'english', label: string) => (
      <div className="sd-row">
        <label className="sd-label" htmlFor={`cl-${key}`}>{label}</label>
        {/* A normal-sized tick at the start of the box column, in a row the height of a standard box. */}
        <span className="cl-tick">
          <input id={`cl-${key}`} type="checkbox" checked={form[key]} disabled={viewOnly}
            onChange={(e) => setForm((f) => withTick(f, key, e.target.checked))} />
        </span>
      </div>
    );
    return (
      <div className="bl-page mlv-host">
        {/* List/Detail switch (as City / Payment Terms Setup): "List View" goes back to the list, asking first when
            there are unsaved changes. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
        </div>
        <div className="bl-grid-area">
          <div className="sd-wrap">
            <div className="sd-head">
              <div>
                <h2>Current Language</h2>
                <div className="sd-bc">
                  <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                  <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
                  <a onClick={leave}>Current Language</a>
                </div>
              </div>
            </div>
            {actions()}
            <div className="sd-body">
              <div className="sd-grid sd-grid--single sd-grid--side sd-grid--square">
                {box('chinese', 'Chinese')}
                {box('english', 'English')}
              </div>
            </div>
            {actions(true)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Current Language</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        {/* Breadcrumb on top, then the List/Detail switch beside the FormTitle (as City / Payment Terms Setup, owner
            request 2026-10-15). "Detail View" opens the ticked record — or the first one — read-only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <ViewToggle view="list" detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== 'detail') return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              const r = rows.find((x) => x.id === id); if (r) open(r, 'view');
            }} />
          <FormTitle pageId="sm-language" auditFormName="Current Language" loadMs={loadMs ?? null} loading={isFetching} />
        </div>
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action bar as legacy: Edit · Refresh · Export to · Search (owner request 2026-10-09). */}
        {!gridMax && (
        <AppToolbar
          onEdit={editSelected} editDisabled={selectedIds.size !== 1}
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <>
              <ExportMenu triggerClassName="ca-btn" filename="CurrentLanguage" headers={['Chinese', 'English']} rows={visible.map((r) => [yesNo(r.chinese), yesNo(r.english)])} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="CurrentLanguage" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<Language> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }}
            onRowClick={(r) => open(r, 'view')}
            loading={isFetching} emptyText="No language record found." hideHeaderTools />
        </div>
      </div>
    </div>
  );
}
